import type { Opacity } from "@color-finder/color-finder-api/models";
import {
  Box,
  Stack,
  SvgIcon,
  type SvgIconProps,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { FC } from "react";
import { ReactComponent as OpaqueSvg } from "./opacity-icons/opaque.svg";
import { ReactComponent as TranslucentSvg } from "./opacity-icons/translucent.svg";
import { ReactComponent as TransparentSvg } from "./opacity-icons/transparent.svg";

const OpaqueIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props} shapeRendering="geometricPrecision">
      <OpaqueSvg />
    </SvgIcon>
  );
};
const TransparentIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props} shapeRendering="geometricPrecision">
      <TransparentSvg />
    </SvgIcon>
  );
};
const TranslucentIcon = (props: SvgIconProps) => {
  return (
    <SvgIcon {...props} shapeRendering="geometricPrecision">
      <TranslucentSvg />
    </SvgIcon>
  );
};

const stateRenderers: {
  [key in Opacity]: (props: SvgIconProps | undefined) => JSX.Element;
} = {
  TRANSPARENT: (props) => (
    <CopyableIcon {...props} Icon={TransparentIcon} titleAccess="Transparent" />
  ),
  TRANSLUCENT: (props) => (
    <CopyableIcon {...props} Icon={TranslucentIcon} titleAccess="Translucent" />
  ),
  OPAQUE: (props) => (
    <CopyableIcon {...props} Icon={OpaqueIcon} titleAccess="Opaque" />
  ),
};

function CopyableIcon({ Icon, ...props }: { Icon: any } & SvgIconProps) {
  return (
    <>
      <Icon {...props}></Icon>
      <Box component="span" sx={visuallyHidden}>
        {props.titleAccess}
      </Box>
    </>
  );
}

export interface ColorStatusProps extends SvgIconProps {
  value: Opacity | string;
  label?: boolean;
}

export const OpacityDisplay: FC<ColorStatusProps> = ({
  value,
  label = false,
  ...props
}) => {
  const renderer = stateRenderers[value as Opacity];
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      {renderer?.(props)}
      {(label || !renderer) && (
        <Typography
          fontSize="inherit"
          fontWeight="inherit"
          textTransform="capitalize"
          component="span"
        >
          {value.toLowerCase()}
        </Typography>
      )}
    </Stack>
  );
};
